import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Save, Shield, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  admin: 'Admin',
  dispatcher: 'Dispatcher',
  sales: 'Sales',
  office_manager: 'Office Manager',
  accounting: 'Accounting',
};

export const PermissionsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editedRoles, setEditedRoles] = useState({});

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/permissions`);
      setData(res.data);
      setEditedRoles(JSON.parse(JSON.stringify(res.data.roles)));
    } catch {
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  const toggleTab = (role, tabId) => {
    if (role === 'admin') return;
    setEditedRoles(prev => {
      const updated = { ...prev };
      const roleTabs = [...(updated[role]?.tabs || [])];
      const idx = roleTabs.indexOf(tabId);
      if (idx >= 0) roleTabs.splice(idx, 1);
      else roleTabs.push(tabId);
      updated[role] = { ...updated[role], tabs: roleTabs };
      return updated;
    });
  };

  const toggleCapability = (role, capId) => {
    if (role === 'admin') return;
    setEditedRoles(prev => {
      const updated = { ...prev };
      updated[role] = { ...updated[role], [capId]: !updated[role]?.[capId] };
      return updated;
    });
  };

  const saveRole = async (role) => {
    if (role === 'admin') return;
    setSaving(role);
    try {
      const perms = editedRoles[role];
      await axios.put(`${API}/permissions`, {
        role,
        tabs: perms.tabs || [],
        can_post_loads: perms.can_post_loads || false,
        can_send_digest: perms.can_send_digest || false,
        can_manage_carriers: perms.can_manage_carriers || false,
        can_manage_users: perms.can_manage_users || false,
        can_manage_all_loads: perms.can_manage_all_loads || false,
      });
      toast.success(`${ROLE_LABELS[role] || role} permissions saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!data) return null;

  const roles = Object.keys(editedRoles).filter(r => r !== 'admin');
  const allTabs = data.all_tabs || [];
  const allCaps = data.all_capabilities || [];

  return (
    <div data-testid="permissions-tab">
      <div className="mb-6">
        <h2 className="text-xl font-black text-zinc-900">Role Permissions</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Control which dashboard tabs and capabilities each role has access to. Admin always has full access.
        </p>
      </div>

      <div className="space-y-6">
        {roles.map(role => {
          const perms = editedRoles[role] || {};
          const hasChanged = JSON.stringify(perms) !== JSON.stringify(data.roles[role]);

          return (
            <div key={role} className="bg-white border border-zinc-200 overflow-hidden" data-testid={`role-card-${role}`}>
              <div className="flex items-center justify-between px-6 py-4 bg-zinc-50 border-b border-zinc-200">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-zinc-400" />
                  <h3 className="font-bold text-zinc-900">{ROLE_LABELS[role] || role}</h3>
                  <span className="text-xs text-zinc-400 bg-zinc-200 px-2 py-0.5 rounded">{role}</span>
                </div>
                <button
                  onClick={() => saveRole(role)}
                  disabled={saving === role || !hasChanged}
                  className={`flex items-center gap-2 px-4 py-1.5 text-sm font-bold uppercase tracking-wide transition-colors ${
                    hasChanged
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-zinc-200 text-zinc-400 cursor-default'
                  } disabled:opacity-50`}
                  data-testid={`save-role-${role}`}
                >
                  {saving === role ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  {hasChanged ? 'Save' : 'Saved'}
                </button>
              </div>

              <div className="p-6">
                <div className="mb-5">
                  <p className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-3">Dashboard Tabs</p>
                  <div className="flex flex-wrap gap-2">
                    {allTabs.map(tab => {
                      const active = (perms.tabs || []).includes(tab.id);
                      return (
                        <button
                          key={tab.id}
                          onClick={() => toggleTab(role, tab.id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border transition-colors ${
                            active
                              ? 'bg-zinc-900 text-white border-zinc-900'
                              : 'bg-white text-zinc-400 border-zinc-200 hover:border-zinc-300'
                          }`}
                          data-testid={`toggle-${role}-${tab.id}`}
                        >
                          {active && <Check className="w-3 h-3" />}
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-3">Capabilities</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {allCaps.map(cap => {
                      const active = perms[cap.id] || false;
                      return (
                        <label
                          key={cap.id}
                          className="flex items-center gap-3 cursor-pointer group"
                          data-testid={`cap-${role}-${cap.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={active}
                            onChange={() => toggleCapability(role, cap.id)}
                            className="w-4 h-4 rounded border-zinc-300 text-red-600 focus:ring-red-500"
                          />
                          <span className={`text-sm ${active ? 'text-zinc-900' : 'text-zinc-400'}`}>
                            {cap.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
